import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { SessionUser } from '@bero/api-client';
import { api } from './client';
import { setActiveSociety } from './active-society';

type SessionCtx = {
  user: SessionUser | null;
  loading: boolean;
  requestCode: (email: string) => Promise<void>;
  verifyCode: (email: string, code: string, fullName?: string, phone?: string) => Promise<{ user: SessionUser; newUser: boolean }>;
  googleSignIn: (idToken: string) => Promise<{ user: SessionUser; newUser: boolean }>;
  appleSignIn: (payload: { identityToken: string; nonce?: string; fullName?: string }) => Promise<{ user: SessionUser; newUser: boolean }>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};

const Ctx = createContext<SessionCtx | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      if (await api.hasSession()) {
        const { user: u } = await api.me();
        setUser(u);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void Promise.resolve().then(() => refresh()); }, [refresh]);

  const requestCode = useCallback(async (email: string) => {
    await api.requestCode(email);
  }, []);

  const verifyCode = useCallback(async (email: string, code: string, fullName?: string, phone?: string) => {
    const r = await api.verifyCode(email, code, fullName, phone);
    // Fresh session must not inherit another account's persisted society choice
    // (it becomes an X-Bero-Society header the new account may not belong to).
    setActiveSociety(null);
    setUser(r.user);
    return r;
  }, []);

  const googleSignIn = useCallback(async (idToken: string) => {
    const r = await api.google(idToken);
    setActiveSociety(null);
    setUser(r.user);
    return r;
  }, []);

  const appleSignIn = useCallback(async (payload: { identityToken: string; nonce?: string; fullName?: string }) => {
    const r = await api.apple(payload);
    setActiveSociety(null);
    setUser(r.user);
    return r;
  }, []);

  const signOut = useCallback(async () => {
    // Log the UI out INSTANTLY (redirect happens on the next render) so the
    // button never feels dead — a slow /logout POST used to block this, making
    // people tap repeatedly. Token + server cleanup then runs in the background,
    // bounded so a hung network can never strand a signed-in session on-screen.
    setUser(null);
    setActiveSociety(null);
    try {
      const { unregisterPush } = await import('./push');
      await Promise.race([
        (async () => { await unregisterPush(); await api.logout(); })(),
        new Promise<void>((resolve) => setTimeout(resolve, 1200)),
      ]);
    } catch { /* best effort */ }
    // Ensure stored tokens are gone even if the logout POST hung past the race,
    // so the next launch can't silently restore the session.
    await api.clearSession();
  }, []);

  const value = useMemo(
    () => ({ user, loading, requestCode, verifyCode, googleSignIn, appleSignIn, signOut, refresh }),
    [user, loading, requestCode, verifyCode, googleSignIn, appleSignIn, signOut, refresh],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}
